import moment from "moment";
import CTA from "Constants";
import i18next from "i18next";
function getCustomerName(customer) {
    if (customer?.fullName) {
        if (customer?.extraData?.lastName) {
            return customer.fullName + " " + customer.extraData.lastName;
        }
        return customer?.fullName;
    }
    if (customer?.phone) {
        return customer?.phone;
    }
    if (customer?.email) {
        return customer?.email;
    }
    return i18next.t("customerUnidentified");
}
function ownerParser(message, selectedChatRef) {
    if (!message.messageFrom || message.messageFrom.includes(CTA.CHATS.MESSAGE_FROM.END_USER)) {
        const name = getCustomerName(selectedChatRef.current.customer);
        if (!selectedChatRef.current.customer || name === selectedChatRef.current?.customer?.id) {
            return i18next.t("ownerNames.user");
        } else {
            return name;
        }
    }
    if (message.messageFrom === CTA.CHATS.MESSAGE_FROM.DEFAULT_ASSISTANT) {
        return i18next.t("ownerNames.agent");
    }
    if (message.messageFrom === CTA.CHATS.MESSAGE_FROM.ASSISTANT && message.assistantUser) {
        if (message.assistantUser.firstName && message.assistantUser.lastName) {
            return `${message.assistantUser.firstName} ${message.assistantUser.lastName}`;
        }
        if (message.assistantUser.email) {
            return message.assistantUser.email;
        }
    }
    return "";
}
function parsePerformingAgent(agent) {
    let parsedPerformingAgent = (agent?.firstName || "") + " " + (agent?.lastName || "")
    if (parsedPerformingAgent.trim() === "") {
        parsedPerformingAgent = i18next.t("serverMessages.theSystem");
    }
    return parsedPerformingAgent
}
function serverMessageParser(message) {
    const server_type = message.messagePayload.serverMessageType;
    const payload = message.messagePayload;
    if (server_type === CTA.SERVER_MESSAGES.OPENED) {
        return i18next.t("serverMessages." + CTA.SERVER_MESSAGES.OPENED);
    }
    if (server_type === CTA.SERVER_MESSAGES.CLOSED_BY_USER) {
        return i18next.t("serverMessages." + CTA.SERVER_MESSAGES.CLOSED_BY_USER, {
            agent:
                (payload.closerAgent?.firstName || "") +
                " " +
                (payload.closerAgent?.lastName || ""),
        });
    }
    if (server_type === CTA.SERVER_MESSAGES.CLOSED_BY_SYSTEM) {
        return i18next.t("serverMessages." + CTA.SERVER_MESSAGES.CLOSED_BY_SYSTEM);
    }
    if (server_type === CTA.SERVER_MESSAGES.OWNER_CHANGE_FROM_BOT_TO_AGENT) {
        return i18next.t("serverMessages." + CTA.SERVER_MESSAGES.OWNER_CHANGE_FROM_BOT_TO_AGENT, {
            agent:
                (payload.latterOwner?.firstName || "") +
                " " +
                (payload.latterOwner?.lastName || ""),
            currentAgent: parsePerformingAgent(payload.ownerChangerAgent)
        });
    }
    if (server_type === CTA.SERVER_MESSAGES.OWNER_CHANGE_FROM_BOT_TO_NONE) {
        return i18next.t("serverMessages." + CTA.SERVER_MESSAGES.OWNER_CHANGE_FROM_BOT_TO_NONE);
    }
    if (server_type === CTA.SERVER_MESSAGES.OWNER_CHANGE_FROM_NONE_TO_AGENT) {
        return i18next.t("serverMessages." + CTA.SERVER_MESSAGES.OWNER_CHANGE_FROM_NONE_TO_AGENT, {
            agent:
                (payload.latterOwner?.firstName || "") +
                " " +
                (payload.latterOwner?.lastName || ""),
            currentAgent: parsePerformingAgent(payload.ownerChangerAgent)
        });
    }
    if (server_type === CTA.SERVER_MESSAGES.OWNER_CHANGE_FROM_AGENT_TO_ANOTHER_AGENT) {
        let parsed = parsePerformingAgent(payload.ownerChangerAgent);
        parsed = parsed.charAt(0).toUpperCase() + parsed.slice(1); 
        return i18next.t(
            "serverMessages." + CTA.SERVER_MESSAGES.OWNER_CHANGE_FROM_AGENT_TO_ANOTHER_AGENT,
            {
                agent:
                    (payload.latterOwner?.firstName || "") +
                    " " +
                    (payload.latterOwner?.lastName || ""),
                currentAgent: parsed
            }
        );
    }
    if (server_type === CTA.SERVER_MESSAGES.OWNER_CHANGE_FROM_AGENT_TO_NONE) {
        return i18next.t("serverMessages." + CTA.SERVER_MESSAGES.OWNER_CHANGE_FROM_AGENT_TO_NONE, {
            currentAgent: parsePerformingAgent(payload.ownerChangerAgent)
        });
    }
    if (server_type === CTA.SERVER_MESSAGES.OWNER_CHANGE_FROM_NONE_TO_NONE) {
        return i18next.t("serverMessages." + CTA.SERVER_MESSAGES.OWNER_CHANGE_FROM_NONE_TO_NONE);
    }
    if (server_type === CTA.SERVER_MESSAGES.OWNER_CHANGE_FROM_NONE_TO_BOT) {
        return i18next.t("serverMessages." + CTA.SERVER_MESSAGES.OWNER_CHANGE_FROM_NONE_TO_BOT);
    }
    if (server_type === CTA.SERVER_MESSAGES.GROUP_ADDED) {
        return i18next.t("serverMessages." + CTA.SERVER_MESSAGES.GROUP_ADDED, {
            group: payload.group.name || "",
            currentAgent: parsePerformingAgent(payload.performingAgent)
        });
    }
    if (server_type === CTA.SERVER_MESSAGES.GROUP_REMOVED) {
        return i18next.t("serverMessages." + CTA.SERVER_MESSAGES.GROUP_REMOVED, {
            group: payload.group?.name || "",
            currentAgent: parsePerformingAgent(payload.performingAgent)
        });
    }
    if (server_type === CTA.SERVER_MESSAGES.TAG_ADDED) {
        return i18next.t("serverMessages." + CTA.SERVER_MESSAGES.TAG_ADDED, {
            tag: payload.conversationTag?.value || "",
            currentAgent: parsePerformingAgent(payload.performingAgent)
        });
    }
    if (server_type === CTA.SERVER_MESSAGES.TAG_REMOVED) {
        return i18next.t("serverMessages." + CTA.SERVER_MESSAGES.TAG_REMOVED, {
            tag: payload.conversationTag?.value || "",
            currentAgent: parsePerformingAgent(payload.performingAgent)
        });
    }
    return null;
}
function messageParser(message, selectedChatRef) {
    const baseUrl = process.env.REACT_APP_API_URL;
    const selectedChatId = selectedChatRef.current.id;
    const payload = message.messagePayload;
    const today = moment();
    const type = message.messageFrom;
    const createdOn = moment(message.createdOn);
    let userType;
    if (
        message.messageFrom.includes(CTA.CHATS.MESSAGE_FROM.ASSISTANT) ||
        message.messageFrom.includes(CTA.CHATS.MESSAGE_FROM.CHAT_BOT)
    ) {
        userType = CTA.CHATS.USER_TYPE.AGENT;
    } else {
        userType = CTA.CHATS.USER_TYPE.USER;
    }
    if (payload.hasOwnProperty("fileName")) {
        let r = new RegExp("^(?:[a-z]+:)?//", "i");
        if (!r.test(payload.fileName)) {
            payload.fileName = baseUrl + payload.fileName;
        }
    }
    const parsedMessage = {
        id: message.id ? message.id : message.createdOn.concat(selectedChatId) + Math.random(),
        userType: userType,
        text: message.deletedOn ? i18next.t("messageDeleted") : payload.text,
        label: payload.label ? payload.label : null,
        attachment: null,
        contentType: payload.type,
        options: payload.options,
        owner: ownerParser(message, selectedChatRef),
        widgetOptions: payload.widgetOptions ? payload.widgetOptions : null,
        fileName: payload.fileName,
        baseUrl: baseUrl,
        createdOn: today.isSame(createdOn, "d")
            ? createdOn.local().format("HH:mm")
            : createdOn.local().format("DD/MM HH:mm"),
        deletedOn: message.deletedOn ? message.deletedOn : null,
        referencedMessage: message.referencedMessage ? message.referencedMessage : null,
    };

    if (
        type === CTA.CHATS.MESSAGE_FROM.ASSISTANT ||
        type.includes(CTA.CHATS.MESSAGE_FROM.CHAT_BOT)
    ) {
        parsedMessage.userType = CTA.CHATS.USER_TYPE.AGENT;
        parsedMessage.messageFrom = CTA.CHATS.USER_TYPE.AGENT;
    }

    if (message.messageFrom === CTA.CHATS.MESSAGE_FROM.SERVER) {
        parsedMessage.messageFrom = CTA.CHATS.MESSAGE_FROM.SERVER;
        parsedMessage.userType = CTA.CHATS.USER_TYPE.AGENT;
        parsedMessage.text = serverMessageParser(message);
    }

    if (message.messageFrom.includes(CTA.CHATS.MESSAGE_FROM.END_USER)) {
        parsedMessage.userType = CTA.CHATS.USER_TYPE.USER;
        parsedMessage.messageFrom = CTA.CHATS.USER_TYPE.USER;
    }

    if (message.messageFrom === CTA.CHATS.MESSAGE_FROM.ASSISTANT_NOTE) {
        parsedMessage.messageFrom = CTA.CHATS.MESSAGE_FROM.ASSISTANT_NOTE;
    }

    if (message.messagePayload.hasOwnProperty("fileName")) {
        if (message.deletedOn) {
            parsedMessage.fileName = null;
        } else {
            let r = new RegExp("^(?:[a-z]+:)?//", "i");
            if (!r.test(message.messagePayload.fileName)) {
                parsedMessage.fileName = baseUrl + message.messagePayload.fileName;
            } else {
                parsedMessage.fileName = message.messagePayload.fileName;
            }
        }
    }

    return parsedMessage;
}

export { messageParser, ownerParser, getCustomerName };
